import { useId, useMemo, useRef, useState } from 'react';
import { childrenOf, childrenWithNoOtherParent, graphOf, other, parentsOf, partnersOf, siblingsOf } from '../../model/graph';
import { defaultPronouns, displayName, newEvent, primaryName, shortName } from '../../model/people';
import { addNewPerson, addRelationship, findRelationship, wouldCreateCycle } from '../../model/tree';
import type { GenderIdentity, Id, ParentType, PartnerStatus, PartnerType, SiblingType, SocialType, Tree } from '../../model/types';
import { PARENT_TYPE_LABELS, PARTNER_STATUS_LABELS, PARTNER_TYPE_LABELS, SIBLING_TYPE_LABELS, SOCIAL_TYPE_LABELS } from '../labels';
import { closeDialog, commit, getState, toast, useApp } from '../store';
import { Field, Modal, Segmented, Select } from './common';
import { DateInput } from './DateInput';
import { GenderPicker } from './GenderPicker';
import { PronounPicker } from './PronounPicker';
import { PersonPicker } from './PersonPicker';

type Kind = 'parent' | 'child' | 'partner' | 'sibling' | 'social';

const TITLES: Record<Kind, string> = {
  parent: 'Add a parent',
  child: 'Add a child',
  partner: 'Add a partner or spouse',
  sibling: 'Add a sibling',
  social: 'Add a friend or other connection',
};

export function AddRelativeDialog({ personId, kind: initialKind, existing: startExisting }: { personId: Id; kind: Kind; existing?: boolean }) {
  const tree = useApp((s) => s.tree)!;
  const person = tree.people[personId];
  const g = graphOf(tree);
  const [kind, setKind] = useState<Kind>(initialKind);
  const [mode, setMode] = useState<'new' | 'existing'>(startExisting ? 'existing' : 'new');
  const [existing, setExisting] = useState<Id | null>(null);
  const [given, setGiven] = useState('');
  const [surname, setSurname] = useState(() => (initialKind === 'child' || initialKind === 'sibling' ? primaryName(person)?.surname ?? '' : ''));
  const [gender, setGender] = useState<GenderIdentity>('unknown');
  const [genderLabel, setGenderLabel] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [born, setBorn] = useState<string | undefined>();
  const [parentType, setParentType] = useState<ParentType>('biological');
  const [partnerType, setPartnerType] = useState<PartnerType>('married');
  const [partnerStatus, setPartnerStatus] = useState<PartnerStatus>('current');
  const [since, setSince] = useState<string | undefined>();
  const [siblingType, setSiblingType] = useState<SiblingType>('unknown');
  const [socialType, setSocialType] = useState<SocialType>('friend');
  const [label, setLabel] = useState('');

  const myParents = parentsOf(g, personId);
  const myPartners = partnersOf(g, personId);
  const currentPartners = (g.partnerRels.get(personId) ?? []).filter((r) => r.status === 'current').map((r) => other(r, personId));
  const [coParents, setCoParents] = useState<Id[]>(() => (currentPartners.length === 1 ? currentPartners : []));
  const [partnerWith, setPartnerWith] = useState<Id[]>(() => (myParents.length === 1 ? myParents : []));
  const [sharedParents, setSharedParents] = useState<Id[]>(myParents);
  // A new partner may also be a parent of this person's children. Children with no other
  // parent recorded are checked to start with; anyone can be unchecked.
  const myChildren = childrenOf(g, personId);
  const [parentOf, setParentOf] = useState<Id[]>(() => childrenWithNoOtherParent(g, personId));
  const [stepParentType, setStepParentType] = useState<ParentType>('biological');
  // Pronouns filled in from the gender, so they can be swapped if the gender changes again.
  const suggested = useRef('');

  const excluded = useMemo(() => [personId], [personId]);
  // When adding someone already in the tree, leave out children they're already a parent of.
  const partnerChildren = myChildren.filter((c) => c !== existing && !(mode === 'existing' && existing && parentsOf(g, c).includes(existing)));
  const genderId = useId();
  const pronounsId = useId();
  if (!person) return null;
  const name = shortName(person);

  const submit = () => {
    let t: Tree = getState().tree!;
    let otherId: Id;
    if (mode === 'existing') {
      if (!existing) return toast('Choose a person first.', 'error');
      otherId = existing;
    } else {
      if (!given.trim() && !surname.trim()) return toast('Enter at least a first or last name.', 'error');
      const r = addNewPerson(t, {
        names: [{ given: given.trim(), surname: surname.trim(), type: 'name' }],
        gender: { identity: gender, label: gender === 'custom' ? genderLabel.trim() || undefined : undefined },
        pronouns: pronouns.trim() || undefined,
        events: born ? [newEvent('birth', { date: born })] : [],
      });
      t = r.tree;
      otherId = r.person.id;
    }
    const newName = displayName(t.people[otherId]);
    const dup = (k: 'parent' | 'partner' | 'sibling' | 'social', a: Id, b: Id) => findRelationship(t, k, a, b);

    switch (kind) {
      case 'parent': {
        if (wouldCreateCycle(t, otherId, personId)) return toast(`${newName} can't be ${name}'s parent: that would make someone their own ancestor.`, 'error');
        if (dup('parent', otherId, personId)) return toast(`${newName} is already ${name}'s parent.`, 'error');
        t = addRelationship(t, { kind: 'parent', a: otherId, b: personId, type: parentType }).tree;
        for (const p of partnerWith) if (p !== otherId && !findRelationship(t, 'partner', p, otherId))
          t = addRelationship(t, { kind: 'partner', a: p, b: otherId, type: partnerType, status: partnerStatus }).tree;
        break;
      }
      case 'child': {
        if (wouldCreateCycle(t, personId, otherId)) return toast(`${newName} can't be ${name}'s child: that would make someone their own ancestor.`, 'error');
        if (dup('parent', personId, otherId)) return toast(`${newName} is already ${name}'s child.`, 'error');
        t = addRelationship(t, { kind: 'parent', a: personId, b: otherId, type: parentType }).tree;
        for (const cp of coParents)
          if (cp !== otherId && !findRelationship(t, 'parent', cp, otherId) && !wouldCreateCycle(t, cp, otherId))
            t = addRelationship(t, { kind: 'parent', a: cp, b: otherId, type: parentType }).tree;
        break;
      }
      case 'partner': {
        if (dup('partner', personId, otherId)) return toast(`${newName} is already ${name}'s partner. Edit that relationship instead.`, 'error');
        t = addRelationship(t, { kind: 'partner', a: personId, b: otherId, type: partnerType, status: partnerStatus, start: since, label: label.trim() || undefined }).tree;
        for (const c of parentOf)
          if (c !== otherId && !findRelationship(t, 'parent', otherId, c) && !wouldCreateCycle(t, otherId, c))
            t = addRelationship(t, { kind: 'parent', a: otherId, b: c, type: stepParentType }).tree;
        break;
      }
      case 'sibling': {
        if (sharedParents.length && siblingType !== 'chosen' && siblingType !== 'step') {
          for (const p of sharedParents)
            if (!findRelationship(t, 'parent', p, otherId) && !wouldCreateCycle(t, p, otherId))
              t = addRelationship(t, { kind: 'parent', a: p, b: otherId, type: siblingType === 'adoptive' ? 'adoptive' : siblingType === 'foster' ? 'foster' : 'biological' }).tree;
          // Shared parents alone can't always tell (e.g. a half-sibling whose other parent isn't recorded), so keep the chosen type.
          const derived = siblingsOf(graphOf(t), personId).find((x) => x.id === otherId)?.type;
          if (siblingType !== 'unknown' && derived !== siblingType && !dup('sibling', personId, otherId))
            t = addRelationship(t, { kind: 'sibling', a: personId, b: otherId, type: siblingType, label: label.trim() || undefined }).tree;
        } else {
          if (dup('sibling', personId, otherId)) return toast(`${newName} is already linked as ${name}'s sibling.`, 'error');
          t = addRelationship(t, { kind: 'sibling', a: personId, b: otherId, type: siblingType, label: label.trim() || undefined }).tree;
        }
        break;
      }
      case 'social': {
        if (dup('social', personId, otherId)) return toast(`${newName} is already connected to ${name}. Edit that connection instead.`, 'error');
        // "Mentor" reads as: the new person mentors this person.
        const [a, b] = socialType === 'mentor' ? [otherId, personId] : [personId, otherId];
        t = addRelationship(t, { kind: 'social', a, b, type: socialType, start: since, label: label.trim() || undefined }).tree;
        break;
      }
    }
    commit(t);
    closeDialog();
    toast(`Added ${newName}.`, 'success');
  };

  const kindSpecific = (() => {
    switch (kind) {
      case 'parent':
        return (
          <>
            <Field label="Type of parent">
              <Select value={parentType} onChange={setParentType} options={PARENT_TYPE_LABELS} />
            </Field>
            {myParents.length > 0 && (
              <Field label="Also record as partner of" hint="Leave unchecked for co-parents who weren't a couple.">
                <div className="row wrap">
                  {myParents.map((p) => (
                    <label key={p} className="check">
                      <input type="checkbox" checked={partnerWith.includes(p)} onChange={(e) => setPartnerWith(e.target.checked ? [...partnerWith, p] : partnerWith.filter((x) => x !== p))} />
                      {displayName(tree.people[p])}
                    </label>
                  ))}
                </div>
              </Field>
            )}
            {partnerWith.length > 0 && (
              <div className="grid-2">
                <Field label="Relationship">
                  <Select value={partnerType} onChange={setPartnerType} options={PARTNER_TYPE_LABELS} />
                </Field>
                <Field label="Status">
                  <Select value={partnerStatus} onChange={setPartnerStatus} options={PARTNER_STATUS_LABELS} />
                </Field>
              </div>
            )}
          </>
        );
      case 'child':
        return (
          <>
            <Field label={`${name}'s role`}>
              <Select value={parentType} onChange={setParentType} options={PARENT_TYPE_LABELS} />
            </Field>
            {myPartners.length > 0 && (
              <Field label="Other parent(s)" hint="Check everyone who is also this child's parent.">
                <div className="row wrap">
                  {myPartners.map((p) => (
                    <label key={p} className="check">
                      <input type="checkbox" checked={coParents.includes(p)} onChange={(e) => setCoParents(e.target.checked ? [...coParents, p] : coParents.filter((x) => x !== p))} />
                      {displayName(tree.people[p])}
                    </label>
                  ))}
                </div>
              </Field>
            )}
          </>
        );
      case 'partner':
        return (
          <>
            <div className="grid-2">
              <Field label="Relationship">
                <Select value={partnerType} onChange={setPartnerType} options={PARTNER_TYPE_LABELS} />
              </Field>
              <Field label="Status">
                <Select value={partnerStatus} onChange={setPartnerStatus} options={PARTNER_STATUS_LABELS} />
              </Field>
            </div>
            <div className="grid-2">
              <Field label="Since">
                <DateInput value={since} onChange={setSince} placeholder="e.g., June 2015" />
              </Field>
              <Field label="Custom label (optional)" hint="e.g., “husband,” “wife,” “spouse,” “partner”">
                <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} />
              </Field>
            </div>
            {partnerChildren.length > 0 && (
              <Field label="Also record as parent of" hint={`Checked: ${name}'s children with no other parent recorded.`}>
                <div className="row wrap">
                  {partnerChildren.map((c) => (
                    <label key={c} className="check">
                      <input type="checkbox" checked={parentOf.includes(c)} onChange={(e) => setParentOf(e.target.checked ? [...parentOf, c] : parentOf.filter((x) => x !== c))} />
                      {displayName(tree.people[c])}
                    </label>
                  ))}
                </div>
              </Field>
            )}
            {partnerChildren.some((c) => parentOf.includes(c)) && (
              <Field label="Type of parent">
                <Select value={stepParentType} onChange={setStepParentType} options={PARENT_TYPE_LABELS} />
              </Field>
            )}
          </>
        );
      case 'sibling':
        return (
          <>
            <Field label="Type of sibling">
              <Select
                value={siblingType}
                onChange={(t) => {
                  setSiblingType(t);
                  // Half-siblings share only some parents, so don't keep all of them checked.
                  if (t === 'half' && myParents.length > 1 && sharedParents.length === myParents.length) setSharedParents([]);
                }}
                options={SIBLING_TYPE_LABELS}
              />
            </Field>
            {myParents.length > 0 && siblingType !== 'chosen' && siblingType !== 'step' && (
              <Field label="Also make them a child of" hint={siblingType === 'half' ? 'Check only the parents they share.' : undefined}>
                <div className="row wrap">
                  {myParents.map((p) => (
                    <label key={p} className="check">
                      <input type="checkbox" checked={sharedParents.includes(p)} onChange={(e) => setSharedParents(e.target.checked ? [...sharedParents, p] : sharedParents.filter((x) => x !== p))} />
                      {displayName(tree.people[p])}
                    </label>
                  ))}
                </div>
              </Field>
            )}
          </>
        );
      case 'social':
        return (
          <>
            <div className="grid-2">
              <Field label="Connection" hint={socialType === 'mentor' ? `They are ${name}'s mentor.` : socialType === 'family-friend' ? `A friend of ${name}'s family.` : undefined}>
                <Select value={socialType} onChange={setSocialType} options={SOCIAL_TYPE_LABELS} />
              </Field>
              <Field label="Since">
                <DateInput value={since} onChange={setSince} placeholder="e.g., 2010" />
              </Field>
            </div>
            <Field label="Custom label (optional)" hint="e.g., “drag mother,” “college roommate,” “house sibling”">
              <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} />
            </Field>
            <div className="faint small-text">For chosen family, add them as a parent or sibling instead, with the type “Chosen parent” or “Chosen sibling.”</div>
          </>
        );
    }
  })();

  return (
    <Modal
      title={`${TITLES[kind]} for ${name}`}
      footer={
        <>
          <button className="btn" onClick={closeDialog}>
            Cancel
          </button>
          <button className="btn primary" onClick={submit}>
            Add
          </button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="row wrap">
          <Segmented
            ariaLabel="Relationship kind"
            value={kind}
            onChange={setKind}
            options={[
              { value: 'parent', label: 'Parent' },
              { value: 'partner', label: 'Partner' },
              { value: 'child', label: 'Child' },
              { value: 'sibling', label: 'Sibling' },
              { value: 'social', label: 'Friend' },
            ]}
          />
        </div>
        <Segmented
          ariaLabel="New or existing person"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'new', label: 'New person' },
            { value: 'existing', label: 'Someone already in the tree' },
          ]}
        />
        {mode === 'existing' ? (
          <Field label="Person">
            <PersonPicker tree={tree} value={existing} onChange={setExisting} exclude={excluded} autoFocus />
          </Field>
        ) : (
          <>
            <div className="grid-2">
              <Field label="First and middle names">
                <input className="input" value={given} onChange={(e) => setGiven(e.target.value)} autoFocus />
              </Field>
              <Field label="Last name">
                <input className="input" value={surname} onChange={(e) => setSurname(e.target.value)} />
              </Field>
            </div>
            <div className="grid-3">
              <Field label="Gender" htmlFor={genderId}>
                <GenderPicker
                  id={genderId}
                  value={gender}
                  onChange={(next) => {
                    setGender(next);
                    // Fill in he/him or she/her if no pronouns are set yet (or the last ones were filled in here).
                    if (!pronouns.trim() || pronouns === suggested.current) {
                      suggested.current = defaultPronouns(next) ?? '';
                      setPronouns(suggested.current);
                    }
                  }}
                />
              </Field>
              <Field label="Pronouns" htmlFor={pronounsId} hint={pronouns && pronouns === suggested.current ? 'Filled in from gender. Change if needed.' : undefined}>
                <PronounPicker id={pronounsId} value={pronouns} onChange={(v) => setPronouns(v ?? '')} />
              </Field>
              <Field label="Born">
                <DateInput value={born} onChange={setBorn} placeholder="optional" />
              </Field>
            </div>
            {gender === 'custom' && (
              <Field label="Self-described gender">
                <input className="input" value={genderLabel} onChange={(e) => setGenderLabel(e.target.value)} placeholder="e.g., Two-Spirit, agender" />
              </Field>
            )}
          </>
        )}
        {kindSpecific}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
